import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { instanceIdFromPathname } from '../utils/companyLogo';
import { fetchMe, invalidateMeCache } from '../lib/meClient';

/** Keep session active_instance_id aligned with `/i/:instanceId` in the URL. */
export function useSyncActiveInstanceFromUrl(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const syncInFlightRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      const data = await fetchMe();
      if (!data || cancelled) return;

      const instances = Array.isArray(data.instances) ? data.instances : [];
      const sessionInstanceId = String(data.activeInstance?.id ?? instances[0]?.id ?? '').trim();
      const urlInstanceId = instanceIdFromPathname(location.pathname);

      if (
        !urlInstanceId ||
        urlInstanceId === 'default' ||
        urlInstanceId === sessionInstanceId ||
        !instances.some((i) => i.id === urlInstanceId)
      ) {
        return;
      }
      if (syncInFlightRef.current) return;

      syncInFlightRef.current = true;
      try {
        const res = await fetch('/api/me/active-instance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ instanceId: urlInstanceId }),
        });
        if (!res.ok || cancelled) return;
        invalidateMeCache();
        try {
          localStorage.setItem('activeInstanceId', urlInstanceId);
        } catch {
          // noop
        }
        const path = location.pathname;
        if (/^\/i\/[^/]+/.test(path)) {
          const nextPath = path.replace(/^\/i\/[^/]+/, `/i/${encodeURIComponent(urlInstanceId)}`);
          if (nextPath !== path) navigate(nextPath, { replace: true });
        }
      } catch (err) {
        console.error('Instance sync from URL failed:', err);
      } finally {
        syncInFlightRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate]);
}
